import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  deleteV1CompaniesCompanyIdJobsId,
  getV1CompaniesCompanyIdJobs,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { TREAD_UUID } from '~hooks/useCompany/useCompany';
import { Job } from '~hooks/useJob/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

export const DeleteJobUtility = () => {
  const { toasterStore } = useStores();

  const [searchValue, setSearchValue] = useState('');
  const [job, setJob] = useState<Job | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleSearch = async () => {
    const trimmed = searchValue.trim();
    if (!trimmed) return;

    setIsSearching(true);
    setJob(null);
    setSearchError(null);

    try {
      const response = await getV1CompaniesCompanyIdJobs({
        path: { 'company-id': TREAD_UUID },
        query: {
          'search[datagrid]': trimmed,
          'page[limit]': 10,
        },
      });

      const rawMatch = response.data.data.find(
        (j) => j.job_id.toUpperCase() === trimmed.toUpperCase(),
      );
      if (rawMatch) {
        setJob(Job.parse(rawMatch));
      } else {
        setSearchError(t('administration.utilities.no_results', { search: trimmed }));
      }
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('administration.utilities.failed_to_search'),
        );
      } catch {
        // handleRequestError throws; fall through to set inline error
      }
      setSearchError(t('administration.utilities.failed_to_search'));
    } finally {
      setIsSearching(false);
    }
  };

  const handleDelete = async () => {
    const companyId = job?.company?.id;
    if (!job || !companyId) return;

    setIsDeleting(true);

    try {
      await deleteV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id: job.id },
      });
      toasterStore.push(
        alert(
          t('administration.utilities.delete_job_success', { jobId: job.jobId }),
          AlertTypes.success,
        ),
      );
      setJob(null);
      setSearchValue('');
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('administration.utilities.failed_to_delete_job'),
        );
      } catch {
        // handleRequestError throws; swallow to avoid unhandled rejection
      }
    } finally {
      setIsDeleting(false);
      setIsConfirmOpen(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const driverName = job?.driverFullName || null;
  const vendorName = job?.vendorJobAssignment?.vendorAccount?.name ?? null;

  return (
    <Paper variant="outlined" sx={{ p: 3, maxWidth: 600 }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={t('administration.utilities.search_hint')}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <IconButton onClick={handleSearch} disabled={isSearching}>
          <SearchIcon />
        </IconButton>
      </Box>
      {searchError && (
        <Typography
          sx={{
            color: 'text.secondary',
            mb: 2,
          }}
        >
          {searchError}
        </Typography>
      )}
      {job && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'bold',
            }}
          >
            {job.jobId}
          </Typography>

          <DetailRow
            label={t('administration.utilities.job_company')}
            value={job.company?.legalName ?? null}
          />
          <DetailRow
            label={t('administration.utilities.customer_account')}
            value={job.order?.account?.name ?? null}
          />
          <DetailRow
            label={t('administration.utilities.created_at')}
            value={dayjs.tz(job.createdAt).format('MMM D, YYYY h:mm A')}
          />
          <DetailRow label={t('administration.utilities.driver')} value={driverName} />
          <DetailRow label={t('administration.utilities.vendor')} value={vendorName} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('administration.utilities.job_state')}:
            </Typography>
            <Chip label={job.status} size="small" />
          </Box>

          <Box sx={{ mt: 2 }}>
            <SmallButton
              color="brandV2Red"
              variant="contained"
              disabled={!job.company}
              onClick={() => setIsConfirmOpen(true)}
            >
              {t('administration.utilities.delete_job')}
            </SmallButton>
          </Box>
        </Box>
      )}
      <ConfirmationDialog
        isBusy={isDeleting}
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleDelete}
        title={t('administration.utilities.delete_job')}
      >
        <Typography>{t('administration.utilities.confirm_delete_job')}</Typography>
        <Typography
          sx={{
            fontWeight: 'bold',
            mt: 1,
          }}
        >
          {job?.jobId}
        </Typography>
      </ConfirmationDialog>
    </Paper>
  );
};

function DetailRow({ label, value }: { label: string; value: string | null }) {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {label}:
      </Typography>
      <Typography variant="body2">{value ?? '—'}</Typography>
    </Box>
  );
}
